import { useCDNLoader } from '@domgen/base-design-system-2.0/hooks/useCDNLoader';
import { CreateAppConfiguration } from './models/createApp';
import { cookieProInitialiser } from './services/cookiePro';
import { log } from '@domgen/shared/src/utils/log';

/**
 * It loads necessary third-party scripts (like adobe tags and cookie pro) after rendering the app.
 * If any critical script fails to load, it logs the error and prevents the app from rendering to avoid a broken user experience.
 * After the app is rendered, it starts loading deferred services like CookiePro and Adobe Tags based on the provided configuration.
 *
 * @param {Partial<CreateAppConfiguration>} deferredConfig - The configuration object for creating the app, including client logo, environment, brand code, version, reCAPTCHA site key, routes, and other service configurations.
 */
export const startDeferredServices = (
  deferredConfig: Partial<
    Pick<CreateAppConfiguration, 'environment' | 'cookiePro' | 'adobeTags' | 'checkoutCDN'>
  >,
) => {
  const { environment, cookiePro, adobeTags, checkoutCDN } = deferredConfig;
  if (cookiePro && cookiePro?.brandScriptId && cookiePro?.src && environment) {
    log.info({ message: 'Initializing CookiePro' });
    const { initialise } = cookieProInitialiser(environment);
    initialise(
      cookiePro?.brandScriptId,
      cookiePro?.src,
      ['sandbox', 'development', 'qa', 'production'],
      'cookiePro',
    );
  }
  if (adobeTags && adobeTags.enabled && adobeTags.scriptUrl && environment) {
    log.info({ message: 'Loading Adobe Tags script' });
    const { loadScript } = useCDNLoader(environment);
    loadScript(adobeTags.scriptUrl, 'adobeTagManager', [
      'sandbox',
      'development',
      'qa',
      'production',
    ]);
  }
  if (checkoutCDN && environment) {
    log.info({ message: 'Loading Checkout SDK script' });
    const { loadScript } = useCDNLoader(environment);
    loadScript(checkoutCDN, 'checkoutSDK', ['sandbox', 'development', 'qa', 'production']);
  }
};
